import React, { useState } from 'react'
import {
  MapPin, TrendingUp, ChevronDown, ChevronUp, Calculator,
  Lightbulb, ArrowRight, ArrowLeftRight, CheckCircle, XCircle,
  Briefcase, Star, Trophy, ChevronRight, Zap, Globe
} from 'lucide-react'

// ── 직업 스토리 시나리오 데이터 ───────────────────────────────────────────────
const SCENARIOS = [
  {
    id: 'weather',
    emoji: '🌦️',
    title: '기상 분석가',
    category: '과학·환경',
    color: 'from-sky-400 to-blue-500',
    bg: 'bg-sky-50',
    border: 'border-sky-200',
    badge: 'bg-sky-100 text-sky-700',
    story: `기상 분석가 지훈씨는 한 달간 서울의 일일 최고기온 자료를 수집했습니다.
30개의 기온 데이터를 도수분포표로 정리하고 히스토그램을 그려
이상 고온이 발생한 날짜를 파악해 기후 경보를 발령합니다.
"데이터를 계급별로 묶으면 전체 분포를 한눈에 볼 수 있어!"`,
    situation: '7월 서울 기온 자료 30개를 계급 크기 2℃로 도수분포표 작성. 22~24℃ 계급에 8일 집중.',
    question: '전체 30일 중 기온이 24℃ 이상인 날의 상대도수는?',
    xLabel: '계급값 (℃)',
    yLabel: '도수 (일)',
    equation: '상대도수 = (해당 계급 도수) ÷ (전체 도수)',
    answer: '24℃ 이상인 날: 7+5+3=15일\n상대도수 = 15 ÷ 30 = 0.5 (50%)',
    whyStat: '기상 분석가는 도수분포표·히스토그램으로 기온 분포를 파악해 폭염·한파 경보를 발령합니다.',
    insight: '기상청·환경부·보험사 리스크 분석가는 기상 자료의 도수분포를 분석해 재해 대비 전략을 수립합니다.',
    realAnswer: 0.5,
    unit: '(상대도수)',
    tableData: [
      { x: '20~22', y: 5 },
      { x: '22~24', y: 8 },
      { x: '24~26', y: 7 },
      { x: '26~28', y: 5 },
      { x: '28~30', y: 3 },
      { x: '30~32', y: 2 },
    ],
    quiz: [
      { q: '계급의 크기가 2℃이고 계급값이 23℃인 계급의 범위는?', a: '22℃ 이상 24℃ 미만', hint: '계급값 = (계급의 양 끝값의 합) ÷ 2. 23 = (22+24)÷2' },
      { q: '도수가 가장 큰 계급의 상대도수는? (전체 30일)', a: '8/30 ≈ 0.267', hint: '22~24℃ 계급의 도수=8. 8÷30=4/15≈0.267' },
    ],
    jobs: ['기상 분석가', '환경 데이터 과학자', '보험 리스크 분석가', '기후 연구원'],
  },
  {
    id: 'nurse',
    emoji: '🏥',
    title: '병원 데이터 분석가',
    category: '의료·보건',
    color: 'from-rose-400 to-pink-500',
    bg: 'bg-rose-50',
    border: 'border-rose-200',
    badge: 'bg-rose-100 text-rose-700',
    story: `병원 데이터 분석가 수아씨는 외래 환자 대기 시간 자료를 분석합니다.
줄기잎 그림으로 50명의 대기 시간(분)을 정리한 결과,
중간값·최빈값을 빠르게 파악해 병원 운영 개선안을 제안합니다.
"줄기잎 그림은 원래 데이터를 보존하면서 분포를 보여주는 강점이 있어!"`,
    situation: '대기 시간(분) 50개를 줄기잎 그림으로 정리. 줄기: 십의 자리, 잎: 일의 자리.',
    question: '줄기가 2인 자료가 10개일 때, 이 계급의 상대도수는? (전체 50명)',
    xLabel: '줄기 (십의 자리)',
    yLabel: '도수 (명)',
    equation: '상대도수 = 해당 도수 ÷ 전체 도수',
    answer: '상대도수 = 10 ÷ 50 = 0.2 (20%)',
    whyStat: '의료 데이터 분석가는 줄기잎 그림으로 환자 대기시간·혈압·체온 등 임상 자료의 분포를 신속하게 파악합니다.',
    insight: '병원·제약회사·공중보건 연구소에서는 줄기잎 그림과 도수분포표로 환자 데이터를 분석해 의료 서비스를 개선합니다.',
    realAnswer: 0.2,
    unit: '(상대도수)',
    tableData: [
      { x: '0~9분', y: 5 },
      { x: '10~19분', y: 12 },
      { x: '20~29분', y: 10 },
      { x: '30~39분', y: 14 },
      { x: '40~49분', y: 7 },
      { x: '50분~', y: 2 },
    ],
    quiz: [
      { q: '줄기잎 그림에서 원래 데이터를 완전히 복원할 수 있나요?', a: '예! 줄기잎 그림은 원래 데이터를 모두 보존합니다. (도수분포표는 일부 정보가 손실됨)', hint: '줄기=십의 자리, 잎=일의 자리. 23은 줄기2|잎3으로 표현' },
      { q: '대기 시간 30~39분 계급의 상대도수는? (전체 50명)', a: '14/50 = 0.28 (28%)', hint: '14 ÷ 50 = 0.28' },
    ],
    jobs: ['병원 데이터 분석가', '보건 통계학자', '임상 연구원', '의료 AI 개발자'],
  },
  {
    id: 'teacher',
    emoji: '📊',
    title: '교육 평가 연구원',
    category: '교육·연구',
    color: 'from-violet-400 to-purple-500',
    bg: 'bg-violet-50',
    border: 'border-violet-200',
    badge: 'bg-violet-100 text-violet-700',
    story: `교육 평가 연구원 민준씨는 중학교 1학년 수학 시험 성적을 분석합니다.
200명의 점수를 도수분포표로 만들고 도수분포다각형을 그려
상위 20%와 하위 20% 학생 수를 파악해 수준별 학습 전략을 수립합니다.
"누적도수를 이용하면 특정 점수 이하 학생 수를 쉽게 파악할 수 있어!"`,
    situation: '수학 점수 200명 자료. 60점 미만: 40명, 60~70: 50명, 70~80: 60명, 80점 이상: 50명.',
    question: '70점 미만인 학생의 누적도수와 누적상대도수는?',
    xLabel: '점수 계급',
    yLabel: '도수 (명)',
    equation: '누적도수 = 처음 계급부터 해당 계급까지의 도수 합',
    answer: '70점 미만 누적도수 = 40 + 50 = 90명\n누적상대도수 = 90 ÷ 200 = 0.45 (45%)',
    whyStat: '교육 평가 연구원은 도수분포다각형과 누적도수로 학생 성취 수준 분포를 파악해 교육과정을 설계합니다.',
    insight: '교육부·한국교육과정평가원·사교육 기업은 대규모 시험 데이터의 누적도수 분석으로 평가 기준을 설정합니다.',
    realAnswer: 90,
    unit: '명',
    tableData: [
      { x: '60점 미만', y: 40 },
      { x: '60~70점', y: 50 },
      { x: '70~80점', y: 60 },
      { x: '80~90점', y: 35 },
      { x: '90점 이상', y: 15 },
    ],
    quiz: [
      { q: '80점 이상인 학생의 상대도수는? (전체 200명)', a: '50/200 = 0.25 (25%)', hint: '80점 이상: 35+15=50명. 50÷200=0.25' },
      { q: '80점 미만인 학생의 누적도수는?', a: '150명', hint: '40+50+60=150명' },
    ],
    jobs: ['교육 평가 연구원', '국가고사 출제위원', '교육 데이터 과학자', '에듀테크 개발자'],
  },
  {
    id: 'marketing',
    emoji: '📱',
    title: '마케팅 데이터 분석가',
    category: '비즈니스·IT',
    color: 'from-amber-400 to-orange-500',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    badge: 'bg-amber-100 text-amber-700',
    story: `마케팅 분석가 예은씨는 앱 사용자 1000명의 하루 사용 시간 자료를 분석합니다.
히스토그램을 그려 사용 패턴을 파악하고,
상대도수가 높은 구간의 사용자에게 맞춤 광고를 제공합니다.
"히스토그램은 연속형 자료의 분포를 막대 그래프로 표현하는 최적의 도구야!"`,
    situation: '앱 사용 시간(분/일) 1000명 자료. 30~60분 구간에 350명(상대도수 0.35) 집중.',
    question: '사용 시간 60분 이상인 사용자의 누적상대도수가 0.4일 때, 60분 미만 사용자 수는?',
    xLabel: '사용 시간 (분)',
    yLabel: '도수 (명)',
    equation: '누적상대도수 = 해당 계급까지의 누적도수 ÷ 전체 도수',
    answer: '60분 미만 누적상대도수 = 1 - 0.4 = 0.6\n60분 미만 사용자 수 = 0.6 × 1000 = 600명',
    whyStat: '마케팅 분석가는 히스토그램과 상대도수로 사용자 행동 패턴을 분석해 개인화 추천·광고 전략을 수립합니다.',
    insight: '카카오·네이버·구글 같은 IT 기업의 데이터 분석가는 사용자 행동 데이터의 분포를 파악해 제품을 개선합니다.',
    realAnswer: 600,
    unit: '명',
    tableData: [
      { x: '0~30분', y: 150 },
      { x: '30~60분', y: 350 },
      { x: '60~90분', y: 250 },
      { x: '90~120분', y: 150 },
      { x: '120~150분', y: 80 },
      { x: '150분~', y: 20 },
    ],
    quiz: [
      { q: '히스토그램과 막대그래프의 차이점은?', a: '히스토그램은 막대 사이에 간격이 없고(연속형 자료), 막대그래프는 간격이 있음(범주형 자료)', hint: '연속형 자료(키·시간)→히스토그램, 범주형(성별·혈액형)→막대그래프' },
      { q: '30~60분 구간의 상대도수는? (전체 1000명)', a: '350/1000 = 0.35 (35%)', hint: '350 ÷ 1000 = 0.35' },
    ],
    jobs: ['마케팅 데이터 분석가', 'UX 리서처', '광고 최적화 엔지니어', '비즈니스 인텔리전스 분석가'],
  },
  {
    id: 'sports',
    emoji: '⚽',
    title: '스포츠 데이터 분석가',
    category: '스포츠·미디어',
    color: 'from-emerald-400 to-green-500',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    badge: 'bg-emerald-100 text-emerald-700',
    story: `스포츠 데이터 분석가 준혁씨는 프로 축구 선수 100명의 경기당 달리기 거리를 분석합니다.
도수분포다각형을 그려 팀별 체력 수준을 비교하고,
상대도수 분포로 리그 평균 대비 자팀 선수들의 경기력을 평가합니다.
"도수분포다각형은 두 집단의 분포를 한 그래프에서 비교할 수 있어!"`,
    situation: '선수 100명의 경기당 달리기 거리(km). 10~11km 구간에 35명 집중. 평균 약 10.2km.',
    question: '달리기 거리 11km 이상인 선수의 상대도수는? (전체 100명, 11km이상 25명)',
    xLabel: '달리기 거리 (km)',
    yLabel: '도수 (명)',
    equation: '상대도수 = 해당 계급 도수 ÷ 전체 도수',
    answer: '상대도수 = 25 ÷ 100 = 0.25 (25%)',
    whyStat: '스포츠 분석가는 선수 퍼포먼스 데이터의 분포를 분석해 훈련 전략과 선발 기준을 수립합니다.',
    insight: '프로 스포츠 구단·스포츠 과학연구소·스포츠 미디어는 선수 데이터 분포 분석으로 경기력 향상과 부상 예방 전략을 세웁니다.',
    realAnswer: 0.25,
    unit: '(상대도수)',
    tableData: [
      { x: '8~9km', y: 10 },
      { x: '9~10km', y: 20 },
      { x: '10~11km', y: 35 },
      { x: '11~12km', y: 20 },
      { x: '12~13km', y: 10 },
      { x: '13km~', y: 5 },
    ],
    quiz: [
      { q: '도수분포다각형에서 각 점의 x좌표는 무엇을 나타내나요?', a: '각 계급의 계급값(양 끝값의 평균)을 x좌표로 사용합니다.', hint: '계급 10~11km의 계급값 = (10+11)÷2 = 10.5km' },
      { q: '10km 미만 선수의 누적상대도수는?', a: '(10+20)/100 = 0.3 (30%)', hint: '8~9km: 10명, 9~10km: 20명. 합=30명. 30÷100=0.3' },
    ],
    jobs: ['스포츠 데이터 분석가', '퍼포먼스 코치', '스포츠 과학자', '스카우팅 분석가'],
  },
]

// ── 직업 탐색 데이터 ──────────────────────────────────────────────────────────
const CAREERS = [
  {
    icon: '🌦️',
    title: '기상·환경 데이터 과학자',
    example: '기온·강수량 도수분포 분석으로 기후 변화 예측',
    formula: '상대도수 = 도수 ÷ 전체도수',
    salary: '연 4,500만~1억원',
    color: 'from-sky-500 to-blue-500',
  },
  {
    icon: '🏥',
    title: '의료·보건 통계학자',
    example: '환자 자료 줄기잎 그림·도수분포표로 임상 분석',
    formula: '누적도수 = Σ(앞 계급 도수 합)',
    salary: '연 4,000만~9,000만원',
    color: 'from-rose-500 to-pink-500',
  },
  {
    icon: '📊',
    title: '교육 평가 연구원',
    example: '시험 성적 분포 분석으로 교육과정·평가 기준 설계',
    formula: '누적상대도수 = 누적도수 ÷ 전체도수',
    salary: '연 4,000만~8,000만원',
    color: 'from-violet-500 to-purple-500',
  },
  {
    icon: '📱',
    title: '마케팅·UX 데이터 분석가',
    example: '사용자 행동 히스토그램으로 개인화 전략 수립',
    formula: '히스토그램 = 계급값 × 도수(막대 넓이×높이)',
    salary: '연 4,500만~1억원',
    color: 'from-amber-500 to-orange-500',
  },
  {
    icon: '⚽',
    title: '스포츠 데이터 분석가',
    example: '선수 퍼포먼스 분포로 훈련 전략·선발 기준 수립',
    formula: '도수분포다각형 = 계급값-도수 좌표 연결',
    salary: '연 3,500만~8,000만원',
    color: 'from-emerald-500 to-green-500',
  },
  {
    icon: '🏛️',
    title: '정부·공공 통계 연구원',
    example: '인구·경제 자료 분포 분석으로 정책 수립 지원',
    formula: '상대도수분포 = 전체를 1로 환산한 비율 분포',
    salary: '연 4,500만~8,500만원',
    color: 'from-indigo-500 to-violet-500',
  },
]

// ── 도전 퀴즈 ─────────────────────────────────────────────────────────────────
const CHALLENGES = [
  {
    id: 'c1',
    emoji: '📋',
    level: '⭐ 쉬움',
    levelColor: 'bg-emerald-100 text-emerald-700',
    question: '어떤 자료의 도수분포표에서 전체 도수가 40이고, 한 계급의 도수가 12일 때 그 계급의 상대도수를 구하시오.',
    hint: '상대도수 = 해당 계급 도수 ÷ 전체 도수 = 12 ÷ 40',
    answer: '상대도수 = 12 ÷ 40 = 0.3',
    explanation: '상대도수 = 12 ÷ 40 = 0.3\n백분율로 나타내면 30%입니다.\n모든 계급의 상대도수의 합은 항상 1이 됩니다.',
  },
  {
    id: 'c2',
    emoji: '🌿',
    level: '⭐⭐ 보통',
    levelColor: 'bg-amber-100 text-amber-700',
    question: '줄기잎 그림에서 줄기가 5인 잎이 1, 3, 5, 7, 9로 5개일 때, 이 자료들의 값을 모두 구하고 중앙값을 구하시오.',
    hint: '줄기5|잎1,3,5,7,9 → 51,53,55,57,59. 홀수개 자료의 중앙값=가운데값',
    answer: '자료: 51, 53, 55, 57, 59. 중앙값 = 55',
    explanation: '줄기 5, 잎 1→51, 3→53, 5→55, 7→57, 9→59\n5개이므로 가운데(3번째) 값인 55가 중앙값입니다.',
  },
  {
    id: 'c3',
    emoji: '📈',
    level: '⭐⭐ 보통',
    levelColor: 'bg-amber-100 text-amber-700',
    question: '전체 50명의 자료에서 어떤 계급까지의 누적도수가 35명이었다. 이 계급의 누적상대도수를 구하고, 이 계급보다 위인 계급들의 누적도수는 몇 명인지 구하시오.',
    hint: '누적상대도수 = 35÷50. 나머지 = 전체 - 35',
    answer: '누적상대도수 = 35÷50 = 0.7. 나머지 계급의 누적도수 = 50-35 = 15명',
    explanation: '누적상대도수 = 35 ÷ 50 = 0.7 (70%)\n전체 50명 중 35명이 이 계급 이하에 속하므로\n나머지 50 - 35 = 15명이 그 위 계급에 속합니다.',
  },
  {
    id: 'c4',
    emoji: '🔬',
    level: '⭐⭐⭐ 어려움',
    levelColor: 'bg-red-100 text-red-700',
    question: '두 반 A, B의 수학 점수 분포를 도수분포다각형으로 나타내려 한다. A반(30명)의 80점 이상 상대도수가 0.4이고, B반(50명)의 80점 이상 도수가 15명일 때, 어느 반의 80점 이상 비율이 더 높은가?',
    hint: 'A반 상대도수=0.4, B반 상대도수=15÷50=0.3. 상대도수로 비교!',
    answer: 'A반(0.4 = 40%) > B반(0.3 = 30%). A반이 더 높다.',
    explanation: 'A반: 상대도수 0.4 → 80점 이상 비율 40%\nB반: 상대도수 = 15 ÷ 50 = 0.3 → 80점 이상 비율 30%\n집단 크기가 다를 때는 반드시 상대도수로 비교해야 합니다!\nA반(40%) > B반(30%) → A반의 비율이 더 높습니다.',
  },
]

const CATEGORY_COLORS: Record<string, string> = {
  '과학·환경': 'bg-sky-100 text-sky-700',
  '의료·보건': 'bg-rose-100 text-rose-700',
  '교육·연구': 'bg-violet-100 text-violet-700',
  '비즈니스·IT': 'bg-amber-100 text-amber-700',
  '스포츠·미디어': 'bg-emerald-100 text-emerald-700',
}

// ── 계산기 컴포넌트 ───────────────────────────────────────────────────────────
type CalcMode = 'relFreq' | 'cumFreq'

interface CalcResult {
  mode: CalcMode
  steps: string[]
  outputVal: number | string
}

function ScenarioCalculator({ scenario }: { scenario: typeof SCENARIOS[0] }) {
  const [mode, setMode] = useState<CalcMode>('relFreq')
  const [freq, setFreq] = useState('')
  const [total, setTotal] = useState('')
  const [result, setResult] = useState<CalcResult | null>(null)
  const [userAnswer, setUserAnswer] = useState('')
  const [checked, setChecked] = useState<boolean | null>(null)

  const reset = () => { setFreq(''); setTotal(''); setResult(null); setUserAnswer(''); setChecked(null) }
  const handleModeChange = (m: CalcMode) => { setMode(m); reset() }

  const calculate = () => {
    const f = parseFloat(freq)
    const t = parseFloat(total)
    if (isNaN(f) || isNaN(t) || t === 0) return

    if (mode === 'relFreq') {
      const rel = Math.round((f / t) * 1000) / 1000
      setResult({
        mode,
        outputVal: rel,
        steps: [
          `공식: 상대도수 = 도수 ÷ 전체도수`,
          `도수 = ${f}, 전체도수 = ${t}`,
          `상대도수 = ${f} ÷ ${t}`,
          `= ${rel} (${Math.round(rel * 100)}%)`,
        ],
      })
    } else {
      // cumFreq: freq는 누적도수, total은 전체도수
      const cum = Math.round((f / t) * 1000) / 1000
      setResult({
        mode,
        outputVal: cum,
        steps: [
          `공식: 누적상대도수 = 누적도수 ÷ 전체도수`,
          `누적도수 = ${f}, 전체도수 = ${t}`,
          `누적상대도수 = ${f} ÷ ${t}`,
          `= ${cum} (${Math.round(cum * 100)}%)`,
        ],
      })
    }
    setUserAnswer('')
    setChecked(null)
  }

  const checkAnswer = () => {
    if (!result) return
    const ua = parseFloat(userAnswer.replace(/,/g, ''))
    const outVal = typeof result.outputVal === 'number' ? result.outputVal : parseFloat(String(result.outputVal))
    const tolerance = Math.abs(outVal) * 0.02 + 0.001
    setChecked(Math.abs(ua - outVal) <= tolerance)
  }

  return (
    <div className="card border-2 border-violet-100">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm font-bold text-slate-700 flex items-center gap-2">
          <Calculator size={15} className="text-violet-600" />
          직접 계산해보기
        </p>
        <div className="flex rounded-lg overflow-hidden border border-slate-200 text-xs">
          <button
            onClick={() => handleModeChange('relFreq')}
            className={`px-3 py-1.5 font-medium transition-colors flex items-center gap-1 ${
              mode === 'relFreq' ? 'bg-violet-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <ArrowRight size={11} />
            상대도수
          </button>
          <button
            onClick={() => handleModeChange('cumFreq')}
            className={`px-3 py-1.5 font-medium transition-colors flex items-center gap-1 ${
              mode === 'cumFreq' ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <ArrowLeftRight size={11} />
            누적상대도수
          </button>
        </div>
      </div>

      <div className="mb-4 bg-slate-50 rounded-xl px-4 py-3 flex items-center gap-3">
        <span className="text-xs text-slate-400 font-medium">공식</span>
        <span className="font-mono font-bold text-violet-700 text-sm">
          {mode === 'relFreq'
            ? '상대도수 = 도수 ÷ 전체도수'
            : '누적상대도수 = 누적도수 ÷ 전체도수'}
        </span>
      </div>

      <div className="flex gap-3 mb-4">
        <div className="flex-1">
          <label className="text-xs font-semibold text-slate-500">
            {mode === 'relFreq' ? '🔢 도수 입력' : '🔢 누적도수 입력'}
          </label>
          <input
            type="number"
            value={freq}
            onChange={e => { setFreq(e.target.value); setResult(null); setChecked(null) }}
            onKeyDown={e => e.key === 'Enter' && calculate()}
            placeholder={mode === 'relFreq' ? '해당 계급 도수' : '누적도수'}
            className="w-full mt-1 text-base border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-300 transition-all"
          />
        </div>
        <div className="flex-1">
          <label className="text-xs font-semibold text-slate-500">🔢 전체도수 입력</label>
          <input
            type="number"
            value={total}
            onChange={e => { setTotal(e.target.value); setResult(null); setChecked(null) }}
            onKeyDown={e => e.key === 'Enter' && calculate()}
            placeholder="전체 도수 합계"
            className="w-full mt-1 text-base border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-300 transition-all"
          />
        </div>
        <div className="flex items-end">
          <button
            onClick={calculate}
            disabled={!freq || !total}
            className={`px-5 py-2.5 rounded-xl font-bold text-sm transition-all ${
              mode === 'relFreq'
                ? 'bg-violet-600 hover:bg-violet-700 text-white disabled:bg-slate-200 disabled:text-slate-400'
                : 'bg-indigo-600 hover:bg-indigo-700 text-white disabled:bg-slate-200 disabled:text-slate-400'
            }`}
          >
            계산!
          </button>
        </div>
      </div>

      {result && (
        <div className="space-y-3">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <p className="text-xs font-bold text-amber-700 mb-3 flex items-center gap-1">
              📝 풀이 과정 (단계별)
            </p>
            <div className="space-y-1.5">
              {result.steps.map((step, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className={`text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    i === result.steps.length - 1 ? 'bg-violet-500 text-white' : 'bg-amber-200 text-amber-700'
                  }`}>{i + 1}</span>
                  <span className={`text-sm font-mono ${
                    i === result.steps.length - 1 ? 'font-black text-violet-700 text-base' : 'text-amber-800'
                  }`}>{step}</span>
                </div>
              ))}
            </div>
          </div>

          <div className={`rounded-xl px-5 py-4 border-2 ${
            mode === 'relFreq' ? 'bg-violet-50 border-violet-300' : 'bg-indigo-50 border-indigo-300'
          }`}>
            <div className="flex items-baseline gap-2">
              <span className={`text-3xl font-black ${mode === 'relFreq' ? 'text-violet-700' : 'text-indigo-700'}`}>
                {result.outputVal}
              </span>
              <span className="text-base font-semibold text-slate-500">
                ({Math.round(Number(result.outputVal) * 100)}%)
              </span>
            </div>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
            <p className="text-xs font-bold text-slate-600 mb-2">🧑‍🎓 답 맞춰보기</p>
            <div className="flex gap-2">
              <input
                type="text"
                value={userAnswer}
                onChange={e => { setUserAnswer(e.target.value); setChecked(null) }}
                onKeyDown={e => e.key === 'Enter' && checkAnswer()}
                placeholder="내 답 (소수로 입력)"
                className={`flex-1 text-sm border-2 rounded-lg px-3 py-2 focus:outline-none transition-all ${
                  checked === null ? 'border-slate-200' : checked ? 'border-emerald-400 bg-emerald-50' : 'border-red-400 bg-red-50'
                }`}
              />
              <button
                onClick={checkAnswer}
                disabled={!userAnswer}
                className="px-4 py-2 rounded-lg bg-slate-700 text-white text-sm font-medium disabled:bg-slate-200 disabled:text-slate-400 hover:bg-slate-800 transition-colors"
              >
                확인
              </button>
            </div>
            {checked !== null && (
              <div className={`mt-2 flex items-center gap-2 text-sm font-semibold ${checked ? 'text-emerald-600' : 'text-red-600'}`}>
                {checked
                  ? <><CheckCircle size={16} /> 정답이에요! 🎉</>
                  : <><XCircle size={16} /> 풀이 과정을 다시 확인해보세요!</>}
              </div>
            )}
          </div>
          <button onClick={reset} className="text-xs text-slate-400 hover:text-slate-600 transition-colors">
            ↺ 다시 입력하기
          </button>
        </div>
      )}
    </div>
  )
}

// ── 퀴즈 컴포넌트 ─────────────────────────────────────────────────────────────
function QuizSection({ quiz }: { quiz: typeof SCENARIOS[0]['quiz'] }) {
  const [revealed, setRevealed] = useState<Record<number, boolean>>({})
  const [showHint, setShowHint] = useState<Record<number, boolean>>({})

  return (
    <div className="space-y-3">
      {quiz.map((q, i) => (
        <div key={i} className="border border-slate-200 rounded-xl p-4 bg-white">
          <p className="text-sm font-semibold text-slate-800 mb-2">❓ {q.q}</p>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setShowHint(h => ({ ...h, [i]: !h[i] }))}
              className="text-xs px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors flex items-center gap-1"
            >
              <Lightbulb size={11} /> 힌트
            </button>
            <button
              onClick={() => setRevealed(r => ({ ...r, [i]: !r[i] }))}
              className="text-xs px-3 py-1.5 rounded-full bg-violet-100 text-violet-700 hover:bg-violet-200 transition-colors"
            >
              {revealed[i] ? '정답 숨기기' : '정답 보기'}
            </button>
          </div>
          {showHint[i] && (
            <div className="mt-2 bg-amber-50 rounded-lg px-3 py-2 text-xs text-amber-700">
              💡 힌트: <span className="font-mono">{q.hint}</span>
            </div>
          )}
          {revealed[i] && (
            <div className="mt-2 bg-violet-50 border border-violet-200 rounded-lg px-3 py-2">
              <span className="text-sm font-bold text-violet-700">✅ 정답: {q.a}</span>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

// ── 챌린지 카드 컴포넌트 ──────────────────────────────────────────────────────
function ChallengeCard({ ch }: { ch: typeof CHALLENGES[0] }) {
  const [showHint, setShowHint] = useState(false)
  const [revealed, setRevealed] = useState(false)

  return (
    <div className="card border border-slate-200">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-2xl">{ch.emoji}</span>
          <span className={`text-xs px-2 py-1 rounded-full font-medium ${ch.levelColor}`}>{ch.level}</span>
        </div>
      </div>
      <p className="text-sm text-slate-800 font-medium leading-relaxed mb-3">{ch.question}</p>
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => setShowHint(h => !h)}
          className="text-xs px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors flex items-center gap-1"
        >
          <Lightbulb size={11} /> 힌트
        </button>
        <button
          onClick={() => setRevealed(r => !r)}
          className="text-xs px-3 py-1.5 rounded-full bg-violet-100 text-violet-700 hover:bg-violet-200 transition-colors flex items-center gap-1"
        >
          <Trophy size={11} /> {revealed ? '정답 숨기기' : '정답 확인'}
        </button>
      </div>
      {showHint && (
        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
          💡 {ch.hint}
        </div>
      )}
      {revealed && (
        <div className="mt-3 bg-violet-50 border border-violet-200 rounded-xl p-3 space-y-2">
          <p className="text-sm font-bold text-violet-700">✅ 정답: {ch.answer}</p>
          <p className="text-xs text-violet-600 leading-relaxed whitespace-pre-line">{ch.explanation}</p>
        </div>
      )}
    </div>
  )
}

// ── 메인 페이지 ───────────────────────────────────────────────────────────────
type Tab = 'scenarios' | 'careers' | 'challenges'

export default function S1_1RealWorldPage() {
  const [selected, setSelected] = useState(SCENARIOS[0])
  const [showTable, setShowTable] = useState(false)
  const [filterCat, setFilterCat] = useState<string>('전체')
  const [activeTab, setActiveTab] = useState<Tab>('scenarios')

  const categories = ['전체', ...Array.from(new Set(SCENARIOS.map(s => s.category)))]
  const filtered = filterCat === '전체' ? SCENARIOS : SCENARIOS.filter(s => s.category === filterCat)

  const tabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: 'scenarios', label: '시나리오 탐색', icon: <MapPin size={14} /> },
    { id: 'careers', label: '직업과 통계', icon: <Briefcase size={14} /> },
    { id: 'challenges', label: '도전 문제', icon: <Trophy size={14} /> },
  ]

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
            <Globe size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">꿈꾸는 직업과 통계</h1>
            <p className="text-sm text-slate-500">기상 분석가, 의료 통계학자, 교육 연구원, 마케터, 스포츠 분석가 — 모두 통계를 활용합니다!</p>
          </div>
        </div>

        <div className="mt-4 rounded-2xl bg-gradient-to-r from-violet-600 to-purple-600 text-white p-5">
          <p className="font-bold text-lg mb-1">🌟 꿈의 직업에서 통계를 만나다!</p>
          <p className="text-sm opacity-90 mb-3">도수분포표·줄기잎 그림·히스토그램·상대도수가 세상 모든 데이터 분석의 기초입니다.</p>
          <div className="grid grid-cols-4 md:grid-cols-5 gap-2">
            {[
              { icon: '🌦️', job: '기상 분석가' },
              { icon: '🏥', job: '의료 통계학자' },
              { icon: '📊', job: '교육 연구원' },
              { icon: '📱', job: '마케팅 분석가' },
              { icon: '⚽', job: '스포츠 분석가' },
            ].map(({ icon, job }) => (
              <div key={job} className="bg-white/20 rounded-xl px-2 py-2 text-center">
                <p className="text-2xl">{icon}</p>
                <p className="text-xs font-bold mt-1">{job}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-slate-100 p-1 rounded-xl w-fit">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-white text-violet-700 shadow-sm'
                : 'text-slate-600 hover:text-slate-800'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── TAB 1: 시나리오 ── */}
      {activeTab === 'scenarios' && (
        <>
          <div className="flex gap-2 mb-4 flex-wrap">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setFilterCat(cat)}
                className={`text-xs px-4 py-2 rounded-full font-medium transition-colors ${
                  filterCat === cat
                    ? 'bg-violet-600 text-white'
                    : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* 좌측: 시나리오 목록 */}
            <div className="space-y-3">
              {filtered.map(scenario => (
                <button
                  key={scenario.id}
                  onClick={() => { setSelected(scenario); setShowTable(false) }}
                  className={`w-full text-left rounded-2xl border-2 p-4 transition-all ${
                    selected.id === scenario.id
                      ? `${scenario.border} ${scenario.bg} shadow-md`
                      : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{scenario.emoji}</span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-slate-800">{scenario.title}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CATEGORY_COLORS[scenario.category]}`}>
                          {scenario.category}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{scenario.situation}</p>
                      <div className="mt-1.5 font-mono text-xs text-violet-600 font-semibold">{scenario.equation}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* 우측: 시나리오 상세 */}
            <div className="lg:col-span-2 space-y-4">
              <div className={`rounded-2xl border-2 ${selected.border} ${selected.bg} p-5`}>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-4xl">{selected.emoji}</span>
                  <div>
                    <h2 className="text-xl font-bold text-slate-800">{selected.title}</h2>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CATEGORY_COLORS[selected.category]}`}>
                      {selected.category}
                    </span>
                  </div>
                </div>

                <div className="mb-4 bg-white/70 rounded-xl p-4 border border-slate-100">
                  <p className="text-xs font-bold text-slate-500 mb-2">📖 이야기로 만나는 통계</p>
                  <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{selected.story}</p>
                </div>

                <p className="text-sm font-semibold text-slate-600 mb-1">📌 수학적 상황</p>
                <p className="text-sm text-slate-700 leading-relaxed mb-3">{selected.situation}</p>

                <div className="bg-white rounded-xl p-4 border border-slate-100">
                  <p className="text-xs font-semibold text-slate-500 mb-3">📐 통계 공식으로 나타내기</p>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-base font-black text-violet-700 font-mono">{selected.equation}</span>
                  </div>
                </div>
              </div>

              <div className="card">
                <p className="text-sm font-semibold text-slate-700 mb-2">💬 문제</p>
                <p className="text-base text-slate-800 font-medium">{selected.question}</p>
                <div className="mt-3 bg-violet-50 border border-violet-200 rounded-xl px-4 py-3">
                  <p className="text-sm font-bold text-violet-700 whitespace-pre-line">✅ 풀이: {selected.answer}</p>
                </div>
                <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                  <p className="text-xs text-blue-700">
                    <span className="font-semibold">왜 통계인가?</span> {selected.whyStat}
                  </p>
                </div>
              </div>

              <div className="card">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <TrendingUp size={15} className="text-violet-500" /> 도수분포표로 보기
                  </p>
                  <button
                    onClick={() => setShowTable(!showTable)}
                    className="text-xs flex items-center gap-1 text-slate-500 hover:text-slate-700 bg-slate-100 px-3 py-1 rounded-full"
                  >
                    {showTable ? '표 접기' : '표 펼치기'}
                    {showTable ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                  </button>
                </div>
                {showTable && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr>
                          <th className="border border-slate-200 px-3 py-2 bg-orange-50 text-orange-700 text-left">{selected.xLabel}</th>
                          <th className="border border-slate-200 px-3 py-2 bg-violet-50 text-violet-700 text-left">{selected.yLabel}</th>
                          <th className="border border-slate-200 px-3 py-2 bg-slate-50 text-slate-600 text-left">상대도수</th>
                          <th className="border border-slate-200 px-3 py-2 bg-indigo-50 text-indigo-700 text-left">누적도수</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selected.tableData.map((row, i) => {
                          const totalFreq = selected.tableData.reduce((s, r) => s + Number(r.y), 0)
                          const cumFreq = selected.tableData.slice(0, i + 1).reduce((s, r) => s + Number(r.y), 0)
                          const relFreq = (Number(row.y) / totalFreq).toFixed(3)
                          return (
                            <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                              <td className="border border-slate-200 px-3 py-2 font-mono font-bold text-orange-600">{row.x}</td>
                              <td className="border border-slate-200 px-3 py-2 font-mono font-bold text-violet-700">{row.y}</td>
                              <td className="border border-slate-200 px-3 py-2 font-mono text-slate-600">{relFreq}</td>
                              <td className="border border-slate-200 px-3 py-2 font-mono text-indigo-600">{cumFreq}</td>
                            </tr>
                          )
                        })}
                        <tr className="bg-slate-100 font-bold">
                          <td className="border border-slate-200 px-3 py-2 text-slate-700">합계</td>
                          <td className="border border-slate-200 px-3 py-2 text-violet-800">
                            {selected.tableData.reduce((s, r) => s + Number(r.y), 0)}
                          </td>
                          <td className="border border-slate-200 px-3 py-2 text-slate-600">1.000</td>
                          <td className="border border-slate-200 px-3 py-2 text-indigo-700">—</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
                {!showTable && (
                  <div className="bg-slate-50 rounded-xl p-4 text-center text-slate-500 text-sm">
                    <p className="text-2xl mb-1">📋</p>
                    <p>표 펼치기를 눌러 도수분포표를 확인해보세요!</p>
                  </div>
                )}
              </div>

              <ScenarioCalculator scenario={selected} />

              <div className="card">
                <p className="text-sm font-semibold text-slate-700 mb-3">🧩 연습 문제</p>
                <QuizSection quiz={selected.quiz} />
              </div>

              <div className="card bg-slate-50 border border-slate-200">
                <p className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <Briefcase size={14} className="text-violet-500" /> 이 상황에서 통계를 쓰는 직업
                </p>
                <div className="flex flex-wrap gap-2">
                  {selected.jobs.map(job => (
                    <span key={job} className="text-xs bg-violet-100 text-violet-700 px-3 py-1.5 rounded-full font-medium flex items-center gap-1">
                      <Star size={10} /> {job}
                    </span>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl bg-gradient-to-r from-slate-800 to-slate-700 text-white p-5">
                <p className="text-sm font-bold mb-1 flex items-center gap-2">
                  <TrendingUp size={15} /> 실제로 어디서 쓰이나요?
                </p>
                <p className="text-sm text-slate-300 leading-relaxed">{selected.insight}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {['도수분포표', '줄기잎 그림', '히스토그램', '상대도수', '누적도수', '도수분포다각형'].map(tag => (
                    <span key={tag} className="text-xs bg-white/10 px-2 py-1 rounded-full">{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 card bg-gradient-to-br from-violet-50 to-purple-50 border-violet-200">
            <h3 className="font-bold text-slate-800 mb-4 text-base">📌 통계 핵심 개념 정리</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { icon: '📋', title: '도수분포표', desc: '계급: 자료를 나누는 구간\n계급의 크기: 구간의 너비\n도수: 각 계급에 속하는 자료 수\n계급값: (최솟값+최댓값)÷2', color: 'text-violet-600' },
                { icon: '📊', title: '상대도수·누적도수', desc: '상대도수 = 도수 ÷ 전체도수\n누적도수 = 처음~해당 계급 도수 합\n누적상대도수 = 누적도수 ÷ 전체도수\n모든 상대도수의 합 = 1', color: 'text-indigo-600' },
                { icon: '📈', title: '그래프 종류', desc: '줄기잎 그림: 원데이터 보존\n히스토그램: 막대 붙어있음(연속)\n도수분포다각형: 계급값-도수 연결\n두 집단 비교는 상대도수 사용!', color: 'text-purple-600' },
              ].map(({ icon, title, desc, color }) => (
                <div key={title} className="bg-white rounded-xl p-4 border border-slate-100">
                  <p className="text-2xl mb-2">{icon}</p>
                  <p className={`font-bold text-sm ${color}`}>{title}</p>
                  <p className="text-xs text-slate-600 mt-1 leading-relaxed whitespace-pre-line">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ── TAB 2: 직업 탐색 ── */}
      {activeTab === 'careers' && (
        <div>
          <div className="mb-6 rounded-2xl bg-gradient-to-r from-violet-600 to-purple-600 text-white p-6">
            <h2 className="text-xl font-bold mb-2">💼 통계를 사용하는 직업들</h2>
            <p className="text-sm text-violet-100">
              도수분포표·줄기잎 그림·히스토그램·상대도수는 기상·의료·교육·IT·스포츠 등 수많은 직업의 핵심 분석 도구입니다!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
            {CAREERS.map(career => (
              <div key={career.title} className="card border border-slate-200 hover:shadow-md transition-shadow">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${career.color} flex items-center justify-center text-2xl mb-3`}>
                  {career.icon}
                </div>
                <h3 className="font-bold text-slate-800 mb-1">{career.title}</h3>
                <p className="text-xs text-slate-500 mb-3 leading-relaxed">{career.example}</p>
                <div className="bg-slate-50 rounded-lg p-3 mb-3">
                  <p className="text-xs text-slate-400 mb-1">실제 사용 공식</p>
                  <code className="text-xs font-mono text-violet-700 font-bold">{career.formula}</code>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs bg-violet-100 text-violet-700 px-2 py-1 rounded-full font-medium">
                    💰 {career.salary}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="card border border-slate-200 bg-gradient-to-br from-slate-800 to-slate-900 text-white mb-6">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <Zap size={20} className="text-yellow-400" /> AI 시대에도 통계가 필요한 이유
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { title: '데이터 사이언스·AI 학습', desc: '머신러닝 모델 학습에 사용하는 데이터의 분포를 히스토그램으로 확인하고 이상값을 제거합니다.', color: 'border-yellow-400' },
                { title: '의료 빅데이터·디지털 헬스', desc: '환자 수천 명의 혈압·혈당 자료를 도수분포표로 분석해 질병 예측 모델을 만듭니다.', color: 'border-rose-400' },
                { title: '스마트 도시·환경 모니터링', desc: '대기질·소음·교통량 센서 데이터를 실시간 히스토그램으로 분석해 도시 환경을 관리합니다.', color: 'border-emerald-400' },
                { title: '금융·보험 리스크 분석', desc: '사고 발생 패턴·주가 변동을 도수분포다각형으로 분석해 보험료와 투자 전략을 산정합니다.', color: 'border-sky-400' },
              ].map(item => (
                <div key={item.title} className={`border-l-4 ${item.color} pl-4 py-2`}>
                  <p className="font-semibold text-sm mb-1">{item.title}</p>
                  <p className="text-xs text-slate-300 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="card border border-violet-200 bg-violet-50">
            <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
              <ChevronRight size={18} className="text-violet-600" /> 통계에서 시작하는 학습 경로
            </h3>
            <div className="flex flex-wrap items-center gap-2">
              {[
                { step: '중1', topic: '자료정리', active: true },
                { step: '중2', topic: '확률', active: false },
                { step: '중3', topic: '통계 심화', active: false },
                { step: '고1', topic: '경우의 수', active: false },
                { step: '고2', topic: '확률분포', active: false },
                { step: '대학', topic: '통계학', active: false },
                { step: '직업', topic: '데이터사이언스', active: false },
              ].map((item, i) => (
                <React.Fragment key={`${item.step}-${item.topic}`}>
                  <div className={`rounded-xl px-3 py-2 text-center ${item.active ? 'bg-violet-600 text-white' : 'bg-white border border-slate-200 text-slate-600'}`}>
                    <p className="text-xs font-bold">{item.step}</p>
                    <p className="text-xs mt-0.5">{item.topic}</p>
                  </div>
                  {i < 6 && <ChevronRight size={14} className="text-slate-400 flex-shrink-0" />}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── TAB 3: 도전 문제 ── */}
      {activeTab === 'challenges' && (
        <div>
          <div className="mb-6 rounded-2xl bg-gradient-to-r from-violet-500 to-purple-600 text-white p-6">
            <h2 className="text-xl font-bold mb-2">🏆 실생활 도전 문제</h2>
            <p className="text-sm text-violet-100">
              실제 상황에서 도수분포표·상대도수·누적도수를 직접 계산해보세요!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
            {CHALLENGES.map(ch => (
              <ChallengeCard key={ch.id} ch={ch} />
            ))}
          </div>

          <div className="card border border-slate-200">
            <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
              <Star size={16} className="text-violet-500" /> 통계 핵심 공식 정리
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { step: '① 도수분포표', desc: '계급의 크기: 구간 너비\n계급값: (하한+상한)÷2\n도수: 각 계급의 자료 수\n(모든 도수의 합 = 전체 도수)', example: '예: 20~30℃, 계급값=25', color: 'bg-violet-50 border-violet-200 text-violet-800' },
                { step: '② 상대도수', desc: '상대도수 = 도수 ÷ 전체도수\n→ 0 이상 1 이하\n모든 상대도수의 합 = 1\n비율(%)로 나타낼 때 ×100', example: '도수=12, 전체=40 → 0.3', color: 'bg-indigo-50 border-indigo-200 text-indigo-800' },
                { step: '③ 누적도수', desc: '누적도수: 처음부터 합산\n누적상대도수 = 누적도수÷전체\n마지막 계급의 누적도수 = 전체\n두 집단 비교→상대도수 사용!', example: '40명→0.3, 누적=0.5', color: 'bg-purple-50 border-purple-200 text-purple-800' },
              ].map(item => (
                <div key={item.step} className={`border rounded-xl p-4 ${item.color}`}>
                  <p className="font-bold text-sm mb-2">{item.step}</p>
                  <p className="text-xs leading-relaxed mb-2 whitespace-pre-line">{item.desc}</p>
                  <div className="bg-white/60 rounded-lg px-2 py-1">
                    <p className="text-xs font-mono font-bold">{item.example}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 bg-violet-50 border border-violet-200 rounded-xl p-4 text-center">
              <p className="text-sm font-bold text-violet-700">자료 수집 → 도수분포표 → 그래프 → 상대도수 → 분석!</p>
              <p className="text-xs text-violet-500 mt-1">통계의 흐름을 이해하면 어떤 자료도 의미 있는 정보로 바꿀 수 있습니다!</p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
